// src/features/blog/types/blogTypes.js
/**
 * ============================================
 * BLOG MODULE - TYPES & CONSTANTS
 * ============================================
 * This file contains all type definitions, enums,
 * and constants used throughout the blog module.
 *
 * @version 1.0.0
 * @author Hashtag Photography Team
 * @lastUpdated 2024-06-28
 * ============================================
 */

/**
 * Blog Post Status Enum
 */
export const BLOG_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
  SCHEDULED: "scheduled",
  ARCHIVED: "archived",
  PENDING_REVIEW: "pending_review",
};

/**
 * Blog Status Labels
 */
export const BLOG_STATUS_LABELS = {
  draft: "Draft",
  published: "Published",
  scheduled: "Scheduled",
  archived: "Archived",
  pending_review: "Pending Review",
};

/**
 * Blog Status Colors
 */
export const BLOG_STATUS_COLORS = {
  draft: "#d9d9d9",
  published: "#52c41a",
  scheduled: "#faad14",
  archived: "#8c8c8c",
  pending_review: "#1890ff",
};

/**
 * Blog Status Icons
 */
export const BLOG_STATUS_ICONS = {
  draft: "📄",
  published: "📢",
  scheduled: "⏰",
  archived: "📦",
  pending_review: "📝",
};

/**
 * Blog Categories
 */
export const BLOG_CATEGORIES = [
  { value: "wedding", label: "Wedding Photography" },
  { value: "portrait", label: "Portrait Photography" },
  { value: "landscape", label: "Landscape Photography" },
  { value: "event", label: "Event Photography" },
  { value: "tips", label: "Photography Tips" },
  { value: "gear", label: "Gear Reviews" },
  { value: "tutorials", label: "Tutorials" },
  { value: "inspiration", label: "Inspiration" },
];

/**
 * Blog Filter Options
 */
export const FILTER_OPTIONS = [
  { key: "status", label: "Status", type: "select" },
  { key: "category", label: "Category", type: "select" },
  { key: "dateRange", label: "Date Range", type: "dateRange" },
  { key: "author", label: "Author", type: "select" },
  { key: "search", label: "Search", type: "text" },
  { key: "tags", label: "Tags", type: "tags" },
];

/**
 * Blog Sort Options
 */
export const SORT_OPTIONS = [
  { value: "createdAt", label: "Created Date" },
  { value: "updatedAt", label: "Updated Date" },
  { value: "publishedAt", label: "Published Date" },
  { value: "title", label: "Title" },
  { value: "viewCount", label: "Views" },
  { value: "likeCount", label: "Likes" },
];

/**
 * Error Messages
 */
export const ERROR_MESSAGES = {
  FETCH_FAILED: "Failed to fetch blog posts. Please try again.",
  CREATE_FAILED: "Failed to create blog post. Please check your inputs.",
  UPDATE_FAILED: "Failed to update blog post. Please try again.",
  DELETE_FAILED: "Failed to delete blog post. Please try again.",
  PUBLISH_FAILED: "Failed to publish blog post. Please try again.",
  UPLOAD_FAILED: "Failed to upload image. Please try again.",
  INVALID_STATUS: "Invalid status transition.",
  SLUG_EXISTS: "Slug already exists. Please use a different URL.",
  TITLE_REQUIRED: "Title is required.",
  CONTENT_REQUIRED: "Content is required.",
  CATEGORY_REQUIRED: "Category is required.",
};

/**
 * Success Messages
 */
export const SUCCESS_MESSAGES = {
  CREATE_SUCCESS: "Blog post created successfully!",
  UPDATE_SUCCESS: "Blog post updated successfully!",
  DELETE_SUCCESS: "Blog post deleted successfully!",
  PUBLISH_SUCCESS: "Blog post published successfully!",
  UPLOAD_SUCCESS: "Image uploaded successfully!",
};

/**
 * Blog Post Structure
 */
export const BLOG_POST_STRUCTURE = {
  id: "",
  title: "",
  slug: "",
  excerpt: "",
  content: "",
  featuredImage: "",
  images: [],
  category: "",
  tags: [],
  status: "draft",
  authorId: "",
  authorName: "",
  authorAvatar: "",
  publishedAt: null,
  scheduledAt: null,
  createdAt: "",
  updatedAt: "",
  viewCount: 0,
  likeCount: 0,
  commentCount: 0,
  metaTitle: "",
  metaDescription: "",
  metaKeywords: [],
  featured: false,
  comments: [],
  seo: {
    title: "",
    description: "",
    keywords: [],
    ogImage: "",
    ogTitle: "",
    ogDescription: "",
  },
  customFields: {},
  isPublic: true,
  allowComments: true,
};

/**
 * Comment Structure
 */
export const COMMENT_STRUCTURE = {
  id: "",
  postId: "",
  author: {
    name: "",
    email: "",
    avatar: "",
  },
  content: "",
  status: "pending",
  createdAt: "",
  parentId: null,
  replies: [],
  likes: 0,
  isApproved: false,
};
